import { useLocalBusinessConsent } from '../../../hooks/useLocalBusinessConsent';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CashCurrentContextV1,
  CashDepositResponseV1,
  CashDepositSourceResponseV1,
  CashOperationLookupV1,
  CashDepositIntentResolutionV1,
} from '@pokerroom/types';
import { api } from '../../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../../store/auth.store';
import { useCashIntent, useCashScope } from './useCashIntent';
import { validateDeposit, type CashIntent } from './cashRecovery';
import { CashRecoveryNotice } from './CashRecoveryNotice';

type Locations = {
  contractVersion: 1;
  sourceWatermark: string;
  businessDate: string;
  sessionId: string;
  locations: {
    id: string;
    kind: string;
    active: boolean;
    eligible: boolean;
    expectedAmount: string;
  }[];
};
export function CashDepositPanel({
  memberId,
  context,
  refresh,
}: {
  memberId: string;
  context: CashCurrentContextV1;
  refresh: () => Promise<unknown>;
}) {
  const { t } = useTranslation('accounting');
  const localBusiness = useLocalBusinessConsent();
  const preparing = useRef(false);
  const scope = useCashScope(memberId);
  const qc = useQueryClient();
  const identity = useAuthStore((s) => JSON.stringify([s.sessionRevision, s.allowedActions]));
  const signature = `${scope}:${identity}:${context.activeSessionId}:${context.currentBusinessDate}`;
  const signatureRef = useRef(signature);
  signatureRef.current = signature;
  const [readyKey, setReadyKey] = useState('');
  async function refreshSnapshot() {
    await qc.refetchQueries({ queryKey: ['cash-context'], type: 'active' }, { throwOnError: true });
    await qc.refetchQueries({ queryKey: ['cash-drawers'], type: 'active' }, { throwOnError: true });
  }
  const cash = useCashIntent(scope, 'deposit');
  const canRead = useAuthStore(
    (s) =>
      !!s.allowedActions?.includes('accounting:read') &&
      !!s.allowedActions?.includes('member:read'),
  );
  const canWrite = useAuthStore((s) => !!s.allowedActions?.includes('member:adjust_points'));
  const [amount, setAmount] = useState(''),
    [note, setNote] = useState(''),
    [drawer, setDrawer] = useState('');
  const [declared, setDeclared] = useState(false),
    [lost, setLost] = useState(false),
    [manualKey, setManualKey] = useState('');
  const [receipt, setReceipt] = useState<CashDepositResponseV1 | null>(null),
    [refreshFailed, setRefreshFailed] = useState(false);
  const locations = useQuery({
    queryKey: ['cash-drawers', scope, context.sourceWatermark],
    enabled: canRead,
    queryFn: () =>
      api.get<Locations>('/accounting/cash-locations', { params: { purpose: 'ZIPA_DEPOSIT' } }),
    retry: false,
  });
  const current =
    context.venueId === sessionVenueKey(useAuthStore.getState().accessToken) &&
    context.sessionState === 'CURRENT_OPEN' &&
    context.guardReasons.length === 0 &&
    locations.data?.contractVersion === 1 &&
    locations.data.sourceWatermark === context.sourceWatermark &&
    locations.data.sessionId === context.activeSessionId &&
    locations.data.businessDate === context.currentBusinessDate;
  const eligible =
    locations.data?.locations.filter((x) => x.kind === 'ZIPA_DRAWER' && x.active && x.eligible) ??
    [];
  function validateSource(p: CashDepositSourceResponseV1, r: CashIntent) {
    return (
      p?.contractVersion === 1 &&
      p.status === 'PREPARED' &&
      p.sourceVersion === 1 &&
      !!p.operationId &&
      !!p.sourceId &&
      p.sourceRef === r.sourceRef &&
      p.memberId === memberId &&
      p.cashLocationId === r.body?.cashLocationId &&
      p.amount === r.body?.amount &&
      p.note === r.body?.note &&
      p.sessionId === (r.sessionId ?? context.activeSessionId) &&
      p.businessDate === (r.businessDate ?? context.currentBusinessDate)
    );
  }
  const [confirmation, setConfirmation] = useState<
    | {
        mode: 'recover';
        record: CashIntent;
        result: Extract<CashDepositIntentResolutionV1, { status: 'RECOVERABLE' }>;
      }
    | { mode: 'void'; record: CashIntent }
    | null
  >(null);
  const [voidReason, setVoidReason] = useState('');
  const confirmationTrigger = useRef<HTMLButtonElement | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    setReadyKey('');
    setReceipt(null);
    setAmount('');
    setNote('');
    setDeclared(false);
    setDrawer('');
    setLost(false);
    setManualKey('');
    setRefreshFailed(false);
    setRefreshing(false);
    setConfirmation(null);
    setVoidReason('');
  }, [signature]);
  const stillCurrent = (r: CashIntent) =>
    signatureRef.current === signature && cash.isCurrent(r.operationKey);
  const available =
    !cash.intent ||
    (['COMMITTED', 'VOIDED'].includes(cash.intent.stage) && readyKey === cash.intent.operationKey);
  async function refreshContext() {
    const result = await refresh();
    if (result && typeof result === 'object' && 'isError' in result && result.isError)
      throw new Error('Member context refresh failed');
    await refreshSnapshot();
  }
  async function accept(p: CashDepositResponseV1, r: CashIntent, consumed = false) {
    setRefreshing(true);
    if (!stillCurrent(r) || !cash.committed(r)) {
      setRefreshing(false);
      return;
    }
    setReadyKey('');
    setReceipt(p);
    if (consumed) {
      setAmount('');
      setNote('');
      setDeclared(false);
    }
    try {
      await refreshContext();
      if (!stillCurrent(r)) return;
      setReadyKey(r.operationKey);
      setRefreshFailed(false);
      cash.setError('');
    } catch {
      if (stillCurrent(r)) setRefreshFailed(true);
    } finally {
      if (signatureRef.current === signature) setRefreshing(false);
    }
  }
  async function prepare() {
    if (
      !canWrite ||
      !current ||
      !declared ||
      !available ||
      refreshing ||
      cash.busy ||
      lost ||
      !eligible.some((x) => x.id === drawer)
    )
      return;
    if (preparing.current) return;
    preparing.current = true;
    let consent;
    try {
      consent = await localBusiness.ask([memberId], 'CASH');
    } catch {
      cash.setError('REFRESH');
      preparing.current = false;
      return;
    }
    preparing.current = false;
    if (!consent) return;
    const operationKey = crypto.randomUUID(),
      sourceRef = crypto.randomUUID();
    const r: CashIntent = {
      recordVersion: 1,
      scope,
      createdAt: Date.now(),
      lastSeenAt: Date.now(),
      action: 'ISSUE_DEPOSIT_SOURCE',
      registrationVersion: 1,
      localBusinessProtocol: 1,
      localInitializationConsent: consent,
      sessionId: context.activeSessionId!,
      businessDate: context.currentBusinessDate,
      operationKey,
      sourceRef,
      stage: 'UNKNOWN',
      body: {
        operationKey,
        sourceRef,
        memberId,
        cashLocationId: drawer,
        amount: `${amount.split('.')[0]!.replace(/^0+(?=\d)/, '')}.${(amount.split('.')[1] ?? '').padEnd(2, '0')}`,
        note: note.trim(),
        expectedSourceWatermark: locations.data!.sourceWatermark,
      },
    };
    await registerAndIssue(r);
  }
  async function issue(r: CashIntent) {
    if (r.localBusinessProtocol === 1 && r.localInitializationOutcome === 'KNOWN_REJECTED') {
      const consent = await localBusiness.ask([memberId], 'CASH');
      if (!consent) return;
      r = { ...r, localInitializationConsent: consent, localInitializationOutcome: undefined };
    }
    await cash.post<CashDepositSourceResponseV1>(
      { ...r, stage: 'UNKNOWN' },
      '/accounting/cash-deposit-sources',
      (value) => validateSource(value, r),
      (p) => {
        if (!stillCurrent(r)) return;
        cash.save(
          {
            ...r,
            action: 'CASH_DEPOSIT',
            issuanceKey: r.operationKey,
            operationKey: crypto.randomUUID(),
            sourceId: p.sourceId,
            sessionId: p.sessionId,
            businessDate: p.businessDate,
            stage: 'PREPARED',
            body: {
              requestVersion: 1,
              type: 'DEPOSIT',
              paymentMethod: 'CASH',
              amount: p.amount,
              note: p.note,
              cashSourceId: p.sourceId,
              cashLocationId: p.cashLocationId,
              expectedSourceVersion: 1,
            },
          },
          r.operationKey,
        );
        setReadyKey('');
      },
      readyKey || undefined,
      r.localBusinessProtocol === 1
        ? { ...r.body, localInitializationConsent: r.localInitializationConsent }
        : undefined,
    );
  }

  function validResolution(p: CashDepositIntentResolutionV1, r: CashIntent) {
    if (!p || !Number.isFinite(Date.parse(p.serverNow))) return false;
    if ('intent' in p)
      return (
        p.intent?.contractVersion === 1 &&
        p.intent.operationKey === r.operationKey &&
        p.intent.sourceRef === r.sourceRef &&
        p.intent.memberId === memberId &&
        (p.status === 'VOIDED'
          ? p.intent.status === 'VOIDED' &&
            !!p.intent.voidedById &&
            !!p.intent.reason &&
            Number.isFinite(Date.parse(p.intent.voidedAt ?? ''))
          : p.intent.status === 'REGISTERED')
      );
    if (p.status !== 'PREPARED' && p.status !== 'COMMITTED') return false;
    const source = p.source;
    return (
      source?.contractVersion === 1 &&
      source.status === 'PREPARED' &&
      source.sourceVersion === 1 &&
      !!source.sourceId &&
      !!source.operationId &&
      source.sourceRef === r.sourceRef &&
      source.memberId === memberId &&
      (!r.body ||
        (source.amount === r.body.amount &&
          source.note === r.body.note &&
          source.cashLocationId === r.body.cashLocationId)) &&
      (!r.sessionId || source.sessionId === r.sessionId) &&
      (!r.businessDate || source.businessDate === r.businessDate) &&
      (p.status !== 'COMMITTED' ||
        validateDeposit(p.deposit, {
          sourceId: source.sourceId,
          sessionId: source.sessionId,
          businessDate: source.businessDate,
        }))
    );
  }
  async function resolve(p: CashDepositIntentResolutionV1, r: CashIntent) {
    if (!stillCurrent(r) || !validResolution(p, r)) throw new Error('Invalid recovery result');
    if (p.status === 'VOIDED') {
      if (cash.voided(r)) {
        setReadyKey('');
        cash.setError('');
        setAmount('');
        setNote('');
        setDeclared(false);
        setDrawer('');
        await refreshContext();
        if (stillCurrent(r)) setReadyKey(r.operationKey);
      }
      return;
    }
    if (p.status === 'REGISTERED') {
      cash.save({ ...r, stage: 'REGISTERED' });
      return;
    }
    if (p.status === 'PREPARED' || p.status === 'COMMITTED') {
      const source = p.source;
      const next: CashIntent = {
        ...r,
        action: 'CASH_DEPOSIT',
        issuanceKey: r.operationKey,
        operationKey:
          p.status === 'COMMITTED' ? p.deposit.originalOperationKey : crypto.randomUUID(),
        sourceId: source.sourceId,
        sessionId: source.sessionId,
        businessDate: source.businessDate,
        stage: 'PREPARED',
        body: {
          requestVersion: 1,
          type: 'DEPOSIT',
          paymentMethod: 'CASH',
          amount: source.amount,
          note: source.note,
          cashSourceId: source.sourceId,
          cashLocationId: source.cashLocationId,
          expectedSourceVersion: 1,
        },
      };
      cash.save(next, r.operationKey);
      if (p.status === 'COMMITTED') await accept(p.deposit, next);
    }
  }
  async function registerAndIssue(r: CashIntent) {
    if (r.localBusinessProtocol === 1 && r.localInitializationOutcome === 'KNOWN_REJECTED') {
      const consent = await localBusiness.ask([memberId], 'CASH');
      if (!consent) return;
      r = { ...r, localInitializationConsent: consent, localInitializationOutcome: undefined };
    }
    if (r.registrationVersion !== 1 || !r.sessionId || !r.businessDate || !r.body) {
      cash.setError('SCOPE_REQUIRED');
      return;
    }
    const result = await cash.post<CashDepositIntentResolutionV1>(
      r,
      '/accounting/cash-deposit-intents',
      (p) => validResolution(p, r),
      (p) => resolve(p, r),
      readyKey || undefined,
      {
        ...r.body,
        ...(r.localBusinessProtocol === 1
          ? { localInitializationConsent: r.localInitializationConsent }
          : {}),
        expectedSessionId: r.sessionId,
        expectedBusinessDate: r.businessDate,
      },
    );
    if (result?.status === 'REGISTERED' && stillCurrent(r))
      await issue({ ...r, stage: 'REGISTERED' });
  }
  async function recheck() {
    const r = cash.intent;
    if (!r?.body || r.registrationVersion !== 1 || !canWrite) return;
    setConfirmation(null);
    await cash.run(async () => {
      const p = await api.post<CashDepositIntentResolutionV1>(
        '/accounting/cash-deposit-intents/recheck',
        r.body,
      );
      if (!stillCurrent(r)) return;
      if (!validResolution(p, r)) throw new Error('Invalid recheck');
      if (p.status === 'RECOVERABLE') {
        if (
          p.intent.sessionId !== r.sessionId ||
          p.intent.businessDate !== r.businessDate ||
          p.intent.amount !== r.body?.amount ||
          p.intent.note !== r.body?.note ||
          p.intent.cashLocationId !== r.body?.cashLocationId
        )
          throw new Error('Recovery identity changed');
        setConfirmation({ mode: 'recover', record: r, result: p });
      } else await resolve(p, r);
    });
  }
  function cancelConfirmation() {
    setConfirmation(null);
    setVoidReason('');
    confirmationTrigger.current?.focus();
  }
  async function confirmRecovery() {
    if (confirmation?.mode !== 'recover' || !canWrite) return;
    const { record, result } = confirmation;
    setConfirmation(null);
    if (!stillCurrent(record) || !record.body) return;
    await issue({
      ...record,
      body: { ...record.body, expectedSourceWatermark: result.sourceWatermark },
    });
  }
  async function confirmVoid() {
    if (confirmation?.mode !== 'void' || !canWrite || !voidReason.trim()) return;
    const r = confirmation.record,
      reason = voidReason.trim();
    setConfirmation(null);
    await cash.run(async () => {
      const p = await api.post<CashDepositIntentResolutionV1>(
        '/accounting/cash-deposit-intents/void',
        { operationKey: r.operationKey, sourceRef: r.sourceRef, memberId, reason },
      );
      if (!stillCurrent(r)) return;
      if (p.status !== 'VOIDED' || !validResolution(p, r))
        throw new Error('Invalid void confirmation');
      await resolve(p, r);
      setVoidReason('');
    });
  }

  async function submit() {
    const r = cash.intent;
    if (!canWrite || !r?.body || r.action !== 'CASH_DEPOSIT' || r.stage === 'COMMITTED') return;
    const frozen = {
      ...r,
      stage: 'UNKNOWN' as const,
      body: { ...r.body, operationKey: r.operationKey },
    };
    await cash.post<CashDepositResponseV1>(
      frozen,
      `/members/${memberId}/zipa`,
      (value) => validateDeposit(value, r) && value.originalOperationKey === r.operationKey,
      (value) => accept(value, frozen, true),
    );
  }
  async function lookup() {
    if (!canRead) return;
    const key = cash.intent?.operationKey ?? manualKey.trim();
    if (!key) {
      setLost(true);
      return;
    }
    const record = cash.intent;
    if (record?.action === 'ISSUE_DEPOSIT_SOURCE' || record?.stage === 'VOIDED') {
      await cash.run(async () => {
        const p = await api.get<CashDepositIntentResolutionV1>(
          `/accounting/cash-deposit-intents/${encodeURIComponent(key)}`,
        );
        if (stillCurrent(record)) await resolve(p, record);
      });
      return;
    }
    await cash.run(async () => {
      const g = await api.get<CashOperationLookupV1>(
        `/accounting/operations/${encodeURIComponent(key)}`,
      );
      if (signatureRef.current !== signature || (record && !cash.isCurrent(record.operationKey)))
        return;
      if (
        g?.contractVersion !== 1 ||
        g.requestedOperationKey !== key ||
        g.originalOperationKey !== key ||
        g.action !== 'CASH_DEPOSIT' ||
        !validateDeposit(
          g.result,
          record ?? {
            sessionId: context.activeSessionId ?? undefined,
            businessDate: context.currentBusinessDate,
          },
        ) ||
        g.result.operationId !== g.operationId ||
        g.result.originalOperationKey !== g.originalOperationKey ||
        g.result.replayed !== false
      ) {
        cash.setError('RECONCILE');
        return;
      }
      if (record) {
        if (!record.issuanceKey || !record.sourceId) {
          cash.setError('RECONCILE');
          return;
        }
        const source = await api.get<CashOperationLookupV1>(
          `/accounting/operations/${encodeURIComponent(record.issuanceKey)}`,
        );
        if (!stillCurrent(record)) return;
        if (
          source?.contractVersion !== 1 ||
          source.action !== 'ISSUE_DEPOSIT_SOURCE' ||
          source.requestedOperationKey !== record.issuanceKey ||
          source.originalOperationKey !== record.issuanceKey ||
          source.result?.contractVersion !== 1 ||
          source.result.status !== 'PREPARED' ||
          source.result.memberId !== memberId ||
          source.result.sourceId !== record.sourceId ||
          source.result.sessionId !== g.result.sessionId ||
          source.result.businessDate !== g.result.businessDate ||
          source.result.sourceRef !== record.sourceRef ||
          source.result.operationId !== source.operationId
        ) {
          cash.setError('RECONCILE');
          return;
        }
      }
      // Manual lookup is read-only and must not replace an active recovery slot.
      if (!record) {
        setReceipt(g.result);
        return;
      }
      await accept(g.result, record, record.stage !== 'COMMITTED');
    });
  }
  useEffect(() => {
    if (canRead && cash.intent && ['COMMITTED', 'VOIDED'].includes(cash.intent.stage))
      void lookup();
  }, [scope, identity, canRead, cash.intent?.operationKey, cash.intent?.stage]);

  if (!canRead || cash.error === 'DENIED') return <CashRecoveryNotice error="DENIED" />;
  return (
    <section
      className="break-words [overflow-wrap:anywhere] space-y-3 rounded-lg border border-base-300 p-4"
      aria-label={t('cash.deposit')}
    >
      {localBusiness.confirmation}
      {cash.intent?.localInitializationOutcome === 'KNOWN_REJECTED' && (
        <p role="alert">{localBusiness.reconfirmMessage}</p>
      )}
      <h3 className="font-semibold">{t('cash.deposit')}</h3>
      {receipt && (
        <p role="status">
          {t('cash.receipt', { balance: receipt.appliedBalance, id: receipt.operationId })}
        </p>
      )}
      {refreshFailed && <p role="alert">{t('cash.refreshFailed')}</p>}
      {cash.busy && <p role="status">{t('cash.processing')}</p>}
      {available && !lost && (
        <>
          <label className="block">
            {t('cash.drawer')}
            <select
              aria-label={t('cash.drawer')}
              className="block min-h-11 w-full max-w-full rounded border border-base-300 bg-base-100 p-2"
              value={drawer}
              onChange={(e) => setDrawer(e.target.value)}
            >
              <option value="">{t('cash.select')}</option>
              {eligible.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.id} ({t('cash.expected', { amount: x.expectedAmount })})
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            {t('cash.amount')}
            <input
              className="block min-h-11 w-full rounded border border-base-300 bg-base-100 p-2"
              aria-label={t('cash.amount')}
              value={amount}
              inputMode="decimal"
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <label className="block">
            {t('cash.note')}
            <input
              className="block min-h-11 w-full rounded border border-base-300 bg-base-100 p-2"
              aria-label={t('cash.note')}
              value={note}
              maxLength={255}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <label className="block">
            <input
              type="checkbox"
              checked={declared}
              onChange={(e) => setDeclared(e.target.checked)}
            />
            {t('cash.declare')}
          </label>
          <button
            className="min-h-11 rounded border border-base-300 px-3 py-2 disabled:opacity-50"
            disabled={
              !canWrite ||
              !current ||
              !declared ||
              !/^\d+(\.\d{1,2})?$/.test(amount) ||
              Number(amount) <= 0 ||
              !note.trim() ||
              !drawer ||
              cash.busy ||
              refreshing
            }
            onClick={() => void prepare()}
          >
            {t('cash.prepare')}
          </button>
          <button
            className="min-h-11 rounded border border-base-300 px-3 py-2 disabled:opacity-50"
            onClick={() => setLost(true)}
          >
            {t('cash.lost')}
          </button>
        </>
      )}
      {cash.intent && (
        <p>
          {t('cash.intent', {
            key: cash.intent.operationKey,
            stage: t(`cash.${cash.intent.stage}`),
          })}
        </p>
      )}
      {cash.intent?.action === 'CASH_DEPOSIT' &&
        cash.intent.body &&
        cash.intent.stage !== 'COMMITTED' && (
          <dl className="break-all rounded border border-base-300 p-3 text-sm">
            <dt>{t('cash.member')}</dt>
            <dd>{memberId}</dd>
            <dt>{t('cash.drawer')}</dt>
            <dd>{String(cash.intent.body.cashLocationId ?? '')}</dd>
            <dt>{t('cash.amount')}</dt>
            <dd>{String(cash.intent.body.amount ?? '')}</dd>
            <dt>{t('cash.note')}</dt>
            <dd>{String(cash.intent.body.note ?? '')}</dd>
            <dt>{t('cash.businessDate')}</dt>
            <dd>{cash.intent.businessDate}</dd>
          </dl>
        )}
      {cash.intent?.action === 'CASH_DEPOSIT' &&
        cash.intent.body &&
        cash.intent.stage !== 'COMMITTED' && (
          <button
            className="min-h-11 rounded border border-base-300 px-3 py-2 disabled:opacity-50"
            disabled={cash.busy || !canWrite}
            onClick={() => void submit()}
          >
            {cash.intent.stage === 'PREPARED' ? t('cash.commit') : t('cash.replay')}
          </button>
        )}
      {cash.intent?.action === 'ISSUE_DEPOSIT_SOURCE' &&
        cash.intent.body &&
        cash.intent.registrationVersion === 1 &&
        cash.intent.stage !== 'VOIDED' && (
          <button
            className="min-h-11 rounded border border-base-300 px-3 py-2 disabled:opacity-50"
            disabled={cash.busy || !canWrite}
            onClick={() => void registerAndIssue(cash.intent!)}
          >
            {t('cash.reissue')}
          </button>
        )}
      {cash.intent?.action === 'ISSUE_DEPOSIT_SOURCE' &&
        (cash.intent.stage !== 'VOIDED' || !available) &&
        canWrite && (
          <div className="space-y-3 rounded border border-base-300 p-3">
            {cash.intent.body && cash.intent.registrationVersion === 1 && (
              <button
                type="button"
                ref={confirmationTrigger}
                className="min-h-11 rounded border border-base-300 px-3 py-2"
                disabled={cash.busy || refreshing}
                onClick={(event) => {
                  confirmationTrigger.current = event.currentTarget;
                  void recheck();
                }}
              >
                {t('cash.recheck')}
              </button>
            )}
            {cash.intent.sourceRef && (
              <button
                type="button"
                className="min-h-11 rounded border border-base-300 px-3 py-2"
                disabled={cash.busy || refreshing}
                onClick={(event) => {
                  confirmationTrigger.current = event.currentTarget;
                  setVoidReason('');
                  setConfirmation({ mode: 'void', record: cash.intent! });
                }}
              >
                {t('cash.voidRequest')}
              </button>
            )}
          </div>
        )}
      {confirmation && (
        <div
          role="group"
          aria-label={t(confirmation.mode === 'void' ? 'cash.voidRequest' : 'cash.recheck')}
          className="space-y-3 rounded border border-warning p-3"
        >
          <p>{t(confirmation.mode === 'void' ? 'cash.voidWarning' : 'cash.recoverWarning')}</p>
          <dl className="break-all text-sm">
            <dt>{t('cash.postKey')}</dt>
            <dd>{confirmation.record.operationKey}</dd>
            <dt>{t('cash.member')}</dt>
            <dd>{memberId}</dd>
            <dt>{t('cash.sourceRef')}</dt>
            <dd>{confirmation.record.sourceRef}</dd>
            {confirmation.mode === 'recover' && (
              <>
                <dt>{t('cash.drawer')}</dt>
                <dd>{confirmation.result.intent.cashLocationId}</dd>
                <dt>{t('cash.amount')}</dt>
                <dd>{confirmation.result.intent.amount}</dd>
                <dt>{t('cash.note')}</dt>
                <dd>{confirmation.result.intent.note}</dd>
                <dt>{t('cash.businessDate')}</dt>
                <dd>{confirmation.result.intent.businessDate}</dd>
              </>
            )}
          </dl>
          {confirmation.mode === 'void' && (
            <label className="block">
              {t('cash.voidReason')}
              <input
                autoFocus
                value={voidReason}
                maxLength={255}
                className="block min-h-11 w-full rounded border border-base-300 bg-base-100 p-2"
                onChange={(e) => setVoidReason(e.target.value)}
              />
            </label>
          )}
          <button
            type="button"
            className="min-h-11 rounded border border-base-300 px-3 py-2"
            disabled={
              cash.busy || !canWrite || (confirmation.mode === 'void' && !voidReason.trim())
            }
            onClick={() => void (confirmation.mode === 'void' ? confirmVoid() : confirmRecovery())}
          >
            {t(confirmation.mode === 'void' ? 'cash.confirmVoid' : 'cash.confirmRecovery')}
          </button>
          <button
            type="button"
            className="min-h-11 rounded border border-base-300 px-3 py-2"
            disabled={cash.busy}
            autoFocus={confirmation.mode === 'recover'}
            onClick={cancelConfirmation}
          >
            {t('cash.cancelRecovery')}
          </button>
        </div>
      )}
      {!cash.intent && (
        <label className="block">
          {t('cash.postKey')}
          <input
            className="block min-h-11 w-full rounded border border-base-300 bg-base-100 p-2"
            aria-label={t('cash.postKey')}
            value={manualKey}
            onChange={(e) => setManualKey(e.target.value)}
          />
        </label>
      )}
      <button
        className="min-h-11 rounded border border-base-300 px-3 py-2 disabled:opacity-50"
        disabled={cash.busy || refreshing}
        onClick={() => void lookup()}
      >
        {t('cash.lookupDeposit')}
      </button>
      <button
        className="min-h-11 rounded border border-base-300 px-3 py-2"
        disabled={cash.busy || refreshing}
        onClick={() =>
          void (cash.intent && ['COMMITTED', 'VOIDED'].includes(cash.intent.stage)
            ? lookup()
            : cash.run(refreshContext))
        }
      >
        {t('cash.refreshSnapshot')}
      </button>
      {(cash.error || (cash.intent && !['COMMITTED', 'VOIDED'].includes(cash.intent.stage))) && (
        <CashRecoveryNotice error={cash.error} />
      )}
      {cash.intent?.stage === 'COMMITTED' && !available && !refreshFailed && !cash.error && (
        <p role="status">{t('cash.verifying')}</p>
      )}
    </section>
  );
}
